import { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MultiSelectFilterProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Optional grouping separator after this index */
  separatorAfterIndex?: number;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  separatorAfterIndex,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);

  // Sync draft when popover opens
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setDraft(selected);
      }
      setOpen(isOpen);
    },
    [selected]
  );

  const allSelected = draft.length === options.length;
  const noneSelected = draft.length === 0;

  const toggleItem = (value: string) => {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleAll = () => {
    setDraft(allSelected ? [] : options.map((o) => o.value));
  };

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft([]);
  };


  const displayLabel = useMemo(() => {
    if (selected.length === 0 || selected.length === options.length) {
      return `All ${label}`;
    }
    return `${label} (${selected.length})`;
  }, [selected, options.length, label]);

  const optionLabelMap = useMemo(
    () => new Map(options.map((o) => [o.value, o.label])),
    [options]
  );

  return (
    <div className="flex flex-col gap-1.5 w-full sm:w-auto">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full sm:w-[180px] justify-between bg-card border-border rounded-[8px] h-10 text-sm font-normal"
          >
            <span className="truncate">{displayLabel}</span>
            <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0 z-50 bg-card rounded-[8px]" align="start">
          <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto">
            {/* Select All */}
            <label className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer text-sm">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
              />
              Select All
            </label>
            <Separator />

            {options.map((option, index) => (
              <div key={option.value}>
                <label className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer text-sm">
                  <Checkbox
                    checked={draft.includes(option.value)}
                    onCheckedChange={() => toggleItem(option.value)}
                  />
                  {option.label}
                </label>
                {separatorAfterIndex !== undefined && index === separatorAfterIndex && (
                  <Separator className="my-1" />
                )}
              </div>
            ))}
          </div>

          <Separator />
          <div className="flex items-center justify-end gap-2 p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={noneSelected}
              className="text-xs"
            >
              Clear
            </Button>
            <Button size="sm" onClick={handleApply} className="text-xs">
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>

    </div>
  );
}
